'use client';
import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { HeroAnimatedStage } from '../components/hero-animated-stage';
import { HeroMobileMarquee } from '../components/hero-mobile-marquee';
import { motion } from 'motion/react';
import { submitWaitlist } from '../services/waitlist';
import { DESKTOP_DESIGN_WIDTH_PX, useFitScale } from '../lib/desktop-scale';
import { inverseScaleStyle } from '../lib/scale-compensate';
import { FieldGroup } from '../components/field';
import { Button } from '../components/button';
import {
  MOBILE_REF_WIDTH_PX,
  mobileRefScale,
} from '../lib/mobile-layout';

/*
 * Desktop hero, authored on the Figma 1440px canvas. The header keeps its real size; everything
 * below it is a 1440×792 frame (888px screen − 96px header) that is scaled to fit the space left in
 * the window, so the composition fills the screen exactly as in the design instead of leaving a
 * blank band on tall or wide windows. Positions below are the Figma ones, relative to the frame.
 */
const DESKTOP_HEADER_H = 96;
const DESKTOP_HERO_FRAME_H = 792;
/** Animated art (cards + people): 680px tall block; cards' top edge sits ~99px into the frame. */
const DESKTOP_STAGE_TOP = 5;
/**
 * Headline: 48px × 144% = 69px tall, centred at y=576 in the frame. The design leaves this text
 * unstyled (raw SemiBold 48px), so it uses the nearest design-system style, text-5xl — capped to
 * /medium (500), not /semibold, since 500 is the heaviest weight used anywhere on the site.
 */
const DESKTOP_TEXT_TOP = 541;

function useMobileRefScale(): number {
  return useSyncExternalStore(
    (onStoreChange) => {
      const handler = () => onStoreChange();
      window.addEventListener('resize', handler);
      return () => window.removeEventListener('resize', handler);
    },
    () => mobileRefScale(window.innerWidth),
    () => 1,
  );
}

export default function HeroSection() {
  // One ref per form: both are always in the DOM (CSS shows one), and a shared ref would end up
  // pointing at whichever mounted last — the hidden desktop form on a phone.
  const waitlistFormRef = useRef<HTMLFormElement>(null)
  const mobileWaitlistFormRef = useRef<HTMLFormElement>(null)
  const [firstName, setFirstName] = useState<string>('')
  const [email, setEmail] = useState<string>('')
  const [showCopy, setShowCopy] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [notice, setNotice] = useState<{ type: 'success' | 'error'; message: string } | null>(null)
  const mobileRefScale = useMobileRefScale()
  const desktopScale = useFitScale(DESKTOP_DESIGN_WIDTH_PX, DESKTOP_HERO_FRAME_H, DESKTOP_HEADER_H)

  // The mobile copy block is rendered at a fixed 430px (MOBILE_REF_WIDTH_PX) and then visually
  // scaled with `transform: scale()` to fit the real viewport — but a transform doesn't change an
  // element's box for layout purposes, so its `overflow-hidden` parent (below) auto-sizes to the
  // block's PRE-transform height, not the (often taller, once scale > 1 on phones wider than 430px)
  // rendered height. That silently clipped the bottom of the block — most visibly the submit button
  // — on any phone wider than the 430px reference. Measuring the real pre-transform height here and
  // setting the parent's height explicitly (scaled, exactly like its width already is) fixes that.
  const mobileCopyRef = useRef<HTMLDivElement>(null)
  const [mobileCopyHeight, setMobileCopyHeight] = useState(0)
  useEffect(() => {
    const el = mobileCopyRef.current
    if (!el) return
    const observer = new ResizeObserver(([entry]) => setMobileCopyHeight(entry.contentRect.height))
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  const scrollToWaitlistForm = (): void => {
    const isDesktop = window.matchMedia('(min-width: 1024px)').matches
    const form = isDesktop ? waitlistFormRef.current : mobileWaitlistFormRef.current
    form?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    form?.querySelector<HTMLInputElement>('input')?.focus({ preventScroll: true })
  }

  const handleJoinWaitlist = async (e?: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e?.preventDefault()
    if (isSubmitting) return
    setNotice(null)
    setIsSubmitting(true)
    const result = await submitWaitlist({ name: firstName, email })
    setIsSubmitting(false)
    if (result.ok) {
      setNotice({ type: 'success', message: "You're on the list. We'll be in touch." })
      setFirstName('')
      setEmail('')
    } else {
      setNotice({ type: 'error', message: result.message })
    }
  }

  const copySpring = {
    type: 'spring' as const,
    stiffness: Math.round(119 * 1.15),
    damping: 25,
    mass: 0.22 * (1 / 1.15),
  }

  return (
    <section className="flex min-h-0 w-full flex-col overflow-x-clip bg-white lg:h-screen">
      {/* Header — logo left, CTA right */}
      {/* items-start, not -center: the "Join waitlist" button (56px) is taller than the logo (38px),
          so centering them shifts the logo ~9px below the header's own padding-top — inconsistent with
          any other page whose header has no button beside the logo (e.g. contact-us). Top-aligning
          both means the logo always sits at exactly padding-top, everywhere. */}
      <header className="relative z-40 flex w-full shrink-0 items-start justify-between max-lg:px-5 max-lg:pb-1 max-lg:pt-[max(1.5rem,env(safe-area-inset-top,0px))] px-[16px] pb-0 pt-[30px] sm:px-[30px] md:px-[40px] md:pt-[40px]">
        <Link href="/">
          <div>
            <Image
              src="/images/proply-logo.svg"
              alt="Proply.io logo"
              width={136}
              height={38}
              className="inline-block h-[38px] w-auto shrink-0 max-lg:h-[30px] max-[380px]:h-[24px]"
            />
          </div>
        </Link>

        <Button type="button" fit="inline" onClick={scrollToWaitlistForm}>
          {isSubmitting ? '…' : 'Join waitlist'}
        </Button>
      </header>

      {/* ───────────── Mobile / tablet: marquee, then copy in a 430px reference frame ───────────── */}
      <div className="relative z-30 mx-auto mt-6 flex w-full max-w-full translate-y-0 justify-center overflow-hidden lg:hidden">
        <div className="relative w-full max-w-full overflow-hidden">
          <HeroMobileMarquee onHumansReveal={() => setShowCopy(true)} />
        </div>
      </div>

      <div className="relative z-20 mx-auto mb-8 w-full translate-y-0 lg:hidden">
        <div
          className="mx-auto overflow-hidden"
          style={{
            width: Math.round(MOBILE_REF_WIDTH_PX * mobileRefScale),
            height: mobileCopyHeight ? Math.round(mobileCopyHeight * mobileRefScale) : undefined,
          }}
        >
          <div
            ref={mobileCopyRef}
            style={{
              width: MOBILE_REF_WIDTH_PX,
              transform: `scale(${mobileRefScale})`,
              transformOrigin: 'top left',
            }}
          >
          <motion.div
            className="flex w-full flex-col items-center bg-white"
            initial={false}
            animate={showCopy ? { opacity: 1, y: 0 } : { opacity: 0, y: 56 }}
            transition={copySpring}
            aria-hidden={!showCopy}
          >
            <h1 className="flex w-full flex-col items-center px-5 text-center text-gray-900">
              <span className="ds-text-3xl-medium capitalize">
                Own nigerian real{' '}estate
              </span>
              <span className="mt-1 flex items-center justify-center gap-[6px] ds-text-3xl-medium capitalize">
                <span>From</span>
                {/* gap-[2px], not the outer row's 6px: the icon stands in for the "₦" glyph itself,
                    so it should hug "10,000" like a currency symbol hugs its digits, not read as a
                    separate word the way "From" does — the wider 6px stays between "From" and this
                    icon+amount pair. */}
                <span className="flex items-center gap-[2px]">
                  {/* h-[26px]/w-[24px]: the asset's own aspect is 44:48 (its right-hand side is
                      trimmed to the glyph's true ink edge, no built-in trailing whitespace); sized to
                      keep roughly the same icon-height:font-size ratio as before (net -16%, +5%, both
                      per explicit requests, rounded to the nearest even px each time), against the
                      ds-text-3xl-medium (30px) this line uses instead of 4xl (36px), so "Own Nigerian
                      Real Estate" fits on one line at every mobile width — see the h1 above. */}
                  <Image
                    src="/images/img_currencyngn.svg"
                    alt="Nigerian Naira currency"
                    width={44}
                    height={48}
                    className="h-[26px] w-[24px] shrink-0"
                  />
                  <span>10,000</span>
                </span>
              </span>
            </h1>

            <div className="mt-4 flex w-full flex-col items-center gap-2 px-5">
              <p className="text-center ds-text-base-normal text-blue-bell-600">
                Proply lets you fund real estate projects that aim to sell upon completion by splitting each project into
                tokens you can buy and in return you receive a share of the profits when the project sells.
              </p>
            </div>
          </motion.div>
          </div>
        </div>

        {/* The form lives OUTSIDE the scaled 430px reference frame above, unlike the headline/copy —
            it's sized with the same real, fixed-height Field/Button components every other form on
            the site uses (56px / 44px below 380px), same as the header's own button. Nesting it
            inside that frame like the copy meant its rendered height rode `mobileRefScale` along
            with everything else — matching the header's button only by coincidence at exactly 430px
            wide, and drifting shorter/taller than it on any other phone or tablet width. */}
        <motion.div
          className="mx-auto mt-4 flex w-full max-w-[430px] flex-col items-center gap-2 px-5"
          initial={false}
          animate={showCopy ? { opacity: 1, y: 0 } : { opacity: 0, y: 56 }}
          transition={copySpring}
          aria-hidden={!showCopy}
        >
          <form
            ref={mobileWaitlistFormRef}
            onSubmit={(e) => void handleJoinWaitlist(e)}
            className="flex w-full flex-col items-center gap-2"
          >
            {/* stacked: below sm (640px — a phone, not just below lg) these squeeze two side-by-side
                fields into a cramped ~half-width each; stacking them lets each keep the full width.
                No `height` prop (that's an unconditional inline style — fixed-56 total height would
                squash two stacked rows into 28px each); instead each field carries its own height
                class, the same "56, dropping to 44 below 380px" scale as every other field/button,
                and the outer pill only fixes a height once fields actually sit side by side (sm:). */}
            <FieldGroup
              variant="light"
              nested
              stacked
              className="sm:h-[56px]"
              fields={[
                {
                  key: 'firstName',
                  type: 'text',
                  placeholder: 'First name',
                  value: firstName,
                  onChange: (e: React.ChangeEvent<HTMLInputElement>) => setFirstName(e.target.value),
                  className: 'h-[56px] px-4 sm:h-full max-[380px]:h-[44px]',
                },
                {
                  key: 'email',
                  type: 'email',
                  placeholder: 'Email address',
                  value: email,
                  onChange: (e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value),
                  className: 'h-[56px] px-4 sm:h-full max-[380px]:h-[44px]',
                },
              ]}
            />

            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? '…' : 'Join waitlist'}
            </Button>
          </form>
          {notice ? (
            <p
              role="status"
              className={`text-center ds-text-base-normal ${
                notice.type === 'success' ? 'text-primary-brand' : 'text-red-700'
              }`}
            >
              {notice.message}
            </p>
          ) : null}
        </motion.div>
      </div>

      {/* ───────────── Desktop: the Figma 1440×792 frame, scaled to fit the window ───────────── */}
      <div className="relative hidden min-h-0 flex-1 lg:block">
        <div
          className="absolute left-1/2 top-1/2"
          style={{
            width: DESKTOP_DESIGN_WIDTH_PX,
            height: DESKTOP_HERO_FRAME_H,
            transform: `translate(-50%, -50%) scale(${desktopScale})`,
            transformOrigin: 'center center',
          }}
        >
          {/* Cards + people: cards sit behind the people, and both fade into the headline below. */}
          <div className="absolute left-0 z-10 w-full" style={{ top: DESKTOP_STAGE_TOP }}>
            <HeroAnimatedStage onHumansReveal={() => setShowCopy(true)} frameScale={desktopScale} />
          </div>

          <motion.div
            className="absolute left-0 z-30 flex w-full flex-col items-center"
            style={{ top: DESKTOP_TEXT_TOP }}
            initial={false}
            animate={showCopy ? { opacity: 1, y: 0 } : { opacity: 0, y: 56 }}
            transition={copySpring}
            aria-hidden={!showCopy}
          >
            {/* Headline, paragraph, and the form below all sit in one counter-scaled block. The
                cards/people above (HeroAnimatedStage) are meant to grow and shrink with the canvas —
                that's the point of scaling the whole 1440px frame to fit the window — but text and
                form controls shouldn't: a button would render taller than 56px on a wide monitor and
                shorter than 56px on a cramped one (breaking the one-height-everywhere rule the site's
                buttons follow elsewhere), and the same "14px" copy would visibly disagree with the
                form's actually-fixed 14px the moment the window isn't exactly 1440px wide. Counter-
                scaling by 1/desktopScale cancels the ancestor's scale back out, so all of it renders
                at one fixed, real-world size regardless of the window — same as the header's button.
                Only this block's TOP position (the ancestor motion.div's `top: DESKTOP_TEXT_TOP`)
                still scales with the frame, so the headline still starts at the same proportional
                spot relative to the art fading in above it. */}
            <div
              className="flex w-full flex-col items-center"
              style={inverseScaleStyle(desktopScale)}
            >
              <h1 className="flex flex-row flex-wrap items-center justify-center gap-[6px] text-center text-gray-900">
                <span className="text-center ds-text-5xl-medium capitalize">
                  Own nigerian real{' '}estate From
                </span>
                <span className="flex items-center gap-[2px]">
                  {/* gap-[2px], not the h1's outer 6px: the icon stands in for the "₦" glyph itself,
                      so it should hug "10,000" like a currency symbol hugs its digits, not sit at
                      word-spacing distance the way "...From" does before it (that 6px lives on the h1).
                      h-[42px]/w-[38px]: net -16% then +5% off the asset's native 48:44, both per
                      explicit requests (rounded to the nearest even px each time), kept at its
                      trimmed 44:48 aspect (the asset's built-in trailing whitespace — it used to
                      reserve ~7.5px of empty space after the glyph inside its 48-wide box — is
                      trimmed out of the SVG itself, so stretching this back to a square box would
                      reopen the gap to "10,000"). */}
                  <Image
                    src="/images/img_currencyngn.svg"
                    alt="Nigerian Naira currency"
                    width={44}
                    height={48}
                    className="h-[42px] w-[38px] shrink-0"
                  />
                  <span className="text-center ds-text-5xl-medium capitalize">
                    10,000
                  </span>
                </span>
              </h1>

              <p className="mt-[2px] text-center ds-text-base-normal text-blue-bell-600 whitespace-nowrap">
                Proply lets you fund real estate projects that aim to sell upon completion by splitting each project into
                <br />
                tokens you can buy and in return you receive a share of the profits when the project sells.
              </p>

              <form
                ref={waitlistFormRef}
                onSubmit={(e) => void handleJoinWaitlist(e)}
                className="mt-[17px] flex w-full flex-row items-center justify-center gap-2 px-[56px]"
              >
                <FieldGroup
                  variant="light"
                  nested
                  height={56}
                  className="max-w-[495px]"
                  fields={[
                    {
                      key: 'firstName',
                      type: 'text',
                      placeholder: 'First name',
                      value: firstName,
                      onChange: (e: React.ChangeEvent<HTMLInputElement>) => setFirstName(e.target.value),
                      className: 'px-6',
                    },
                    {
                      key: 'email',
                      type: 'email',
                      placeholder: 'Email address',
                      value: email,
                      onChange: (e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value),
                      className: 'px-6',
                    },
                  ]}
                />

                <Button type="submit" fit="inline" disabled={isSubmitting}>
                  {isSubmitting ? '…' : 'Join waitlist'}
                </Button>
              </form>
              {notice ? (
                <p
                  role="status"
                  className={`mt-3 text-center ds-text-base-normal ${
                    notice.type === 'success' ? 'text-primary-brand' : 'text-red-700'
                  }`}
                >
                  {notice.message}
                </p>
              ) : null}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  )
}
